import React, {useEffect} from 'react';
import {SignupForm, FormLayout, SpecialistSignupForm} from '@/components/auth-screen';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {Routes as WebRoutes} from '@/navigation/Routes';
import {cn, resetMessage} from '@/utils/common_functions';
import {Text} from '@/ui-kits';
import { authFailureStatus, authSuccessStatus } from '@/services/redux/selectors';
import { ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes } from '@/utils/getMessages';
import { useToast } from '@/hooks';
import { resetAuthMessage, resetAuthSession } from '@/services/redux/slices/authSlice';

export const SignupScreen: React.FC = () => {
  // ================================
  // hooks
  // ================================
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {showToast} = useToast();

  // ================================
  // selectors
  // ================================
  const authSuccess = useSelector(authSuccessStatus) as SuccessCodes;
  const authFailure = useSelector(authFailureStatus) as ErrorCodes;
  
  // ================================
  // states
  // ================================
  const isPatient = location.pathname === WebRoutes.SIGNUP;

  // ================================
  // functions
  // ================================
  function handleSuccess() {
    navigate(WebRoutes.VERIFY_OTP);
  }

  function gotoPatientSignup() {
    navigate(WebRoutes.SIGNUP);
    dispatch(resetAuthSession());
  }
  function gotoSpecialistSignup() {
    navigate(WebRoutes.SPECIALIST_SIGNUP);
    dispatch(resetAuthSession());
  }


  // ================================
  // side effects
  // ================================
  useEffect(() => {
    if (authSuccess) {
      if (authSuccess === 'S-10002') {
        showToast(getSuccessMessage(authSuccess), 'success');
        handleSuccess();
      }
    }
    if (authFailure) {
      showToast(getErrorMessage(authFailure), 'failure');
    }
    // clean up
    return () =>
      resetMessage(() => dispatch(resetAuthMessage()));
  }, [authSuccess, authFailure]);

  return (
    <FormLayout className={cn(!isPatient ? "max-w-[700px]" : "")}>
      <div className="flex justify-center p-4 lg:p-8 gap-4 items-center mb-8 border-border absolute top-0 right-1/2 translate-x-1/2 translate-y-[-40%] z-[1]">
        <Tab label="Patient" active={isPatient} onPress={gotoPatientSignup} />
        <Tab label="Specialist" active={!isPatient} onPress={gotoSpecialistSignup} />
      </div>

      {isPatient ? <SignupForm /> : <SpecialistSignupForm />}
    </FormLayout>
  );
};

interface TabProps {
  label: string;
  active: boolean;
  onPress: () => void;
}

const Tab: React.FC<TabProps> = props => {
  const {label, active, onPress} = props;
  return (
    <button
      style={{boxShadow: '0px 4px 6px rgba(0,0,0,0.1)'}}
      className={cn(
        'flex flex-col p-2 lg:p-4 rounded-lg w-[130px] sm:w-[160px] md:w-[190px] lg:w-[220px]',
        active ? 'bg-gradient-to-r from-ui_blue to-ui_blue_light' : 'bg-white',
      )}
      onClick={onPress}>
      <Text variant="subtitle" className={cn('font-FigtreeMeduim text-center w-full', active ? 'text-white' : 'text-ui_blue')}>
        {label}
      </Text>
    </button>
  );
};